import { CSSProperties } from 'react';
import { useCustomTranslation } from '../../../../../hooks';
import { HistoricalParallelMachinePairsMatrixData } from './useHistoricalParallelMachinePairs';

type HistoricalParallelMachinePairsMatrixProps = HistoricalParallelMachinePairsMatrixData;

const heatBackground = (alpha: number) =>
  `color-mix(in srgb, var(--color-navy60) ${Math.round(alpha * 100)}%, transparent)`;

const heatStyle = (frequency: number, maxFrequency: number): CSSProperties => {
  if (frequency === 0 || maxFrequency === 0) {
    return {};
  }
  const intensity = frequency / maxFrequency;
  return {
    backgroundColor: heatBackground(0.15 + intensity * 0.85),
    color: intensity >= 0.5 ? 'var(--color-white100)' : 'var(--color-navy90)',
  };
};

export const HistoricalParallelMachinePairsMatrix = ({
  machines,
  maxFrequency,
  getFrequency,
}: HistoricalParallelMachinePairsMatrixProps) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan.autoAssigment');

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex-1 overflow-auto rounded-xs border border-border-container">
        <table className="min-w-full border-collapse text-s11">
          <thead>
            <tr>
              <th className="sticky top-0 left-0 z-20 min-w-36 border-r border-b border-border-container bg-surface-secondary px-2 py-2 text-left text-s11-sb" />
              {machines.map((machine) => (
                <th
                  className="sticky top-0 z-10 max-w-28 border-b border-border-container bg-surface-secondary px-2 py-2 text-left text-s11-sb"
                  key={machine.machineId}
                  title={machine.tag}
                >
                  <span className="block truncate">{machine.tag}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {machines.map((rowMachine) => (
              <tr key={rowMachine.machineId}>
                <th
                  className="sticky left-0 z-10 max-w-36 border-r border-b border-border-container bg-surface-secondary px-2 py-2 text-left text-s11-sb"
                  title={rowMachine.tag}
                >
                  <span className="block truncate">{rowMachine.tag}</span>
                </th>
                {machines.map((columnMachine) => {
                  const isDiagonal = rowMachine.machineId === columnMachine.machineId;
                  const frequency = isDiagonal ? 0 : getFrequency(rowMachine.machineId, columnMachine.machineId);

                  return (
                    <td
                      className="border-b border-border-container px-2 py-1.5 text-center tabular-nums"
                      key={columnMachine.machineId}
                      style={heatStyle(frequency, maxFrequency)}
                      title={
                        frequency > 0
                          ? tWrapper('policy.parallelPairs.matrixCellAllowed', {
                              firstMachine: rowMachine.tag,
                              secondMachine: columnMachine.tag,
                            })
                          : undefined
                      }
                    >
                      {isDiagonal ? <span className="text-text-disabled">—</span> : frequency > 0 ? frequency : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 text-s11 text-text-secondary">
        <span>{tWrapper('policy.parallelPairs.heatLegendLow')}</span>
        <span
          className="inline-block h-3 w-24 rounded-xs"
          style={{ background: `linear-gradient(to right, ${heatBackground(0.15)}, ${heatBackground(1)})` }}
        />
        <span>{tWrapper('policy.parallelPairs.heatLegendHigh')}</span>
      </div>
    </div>
  );
};
